import { inviteApi, invitationApi, guestApi } from '../../utils/urls';
import { rootApi } from '../../services/rootApi';


export const invitesApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    generateGiftCodes: builder.mutation({
      query: ({ amount, courseId, objectType, year }) => ({
        url: invitationApi(),
        method: 'POST',
        body: {
          data: { objectId: courseId, objectType: objectType || 'course', n: +amount, year},
          method: 'generateGiftCodes'
        },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: [{ type: 'InviteList', id: 'LIST' }],
    }),
    checkInvitation: builder.mutation({
      query: ({ hash, giftCode }) => ({
        url: guestApi(),
        method: 'POST',
        body: {
          data: { hash, giftCode },
          method: 'checkInvitation'
        },
        // Prevent baseQuery from showing a generic error toast. We'll handle errors in the actions/UI layer.
        suppressGenericErrorToast: true,
      }),
      transformResponse: (response) => response.data,
    }),
    acceptInvitation: builder.mutation({
      query: ({ hash, giftCode }) => ({
        url: guestApi(),
        method: 'POST',
        body: {
          data: { hash, giftCode },
          method: 'acceptInvitation'
        },
      }),
      transformResponse: (response) => response.data,
    }),
    fetchInvitations: builder.query({
      query: ({ searchText }) => ({
        url: invitationApi(),
        method: 'POST',
        body: {
          data: searchText ? { searchText } : {},
          method: 'prepareForUserList',
          pageLength: 1000,
          pageStart: 0,
        },
      }),
      transformResponse: (response) => response.data,
      providesTags: (result) => [
        { type: 'InviteList', id: 'LIST' },
        ...(result ?? []).map((invite) => ({ type: 'Invite', id: invite.id })),
      ],
    }),
    fetchInviteById: builder.query({
      query: ({ id }) => ({
        url: invitationApi(),
        method: 'POST',
        body: {
          data: { id },
          method: 'showInfo'
        },
      }),
      transformResponse: (response) => response.data,
      providesTags: (result, error, { id }) => [{ type: 'Invite', id }],
    }),
    deleteInvite: builder.mutation({
      query: (id) => ({
        url: invitationApi(),
        method: 'POST',
        body: {
          data: { id },
          method: 'delete'
        },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: [{ type: 'InviteList', id: 'LIST' }],
    }),
    resendInvite: builder.mutation({
      query: (id) => ({
        url: invitationApi(),
        method: 'POST',
        body: {
          data: { id },
          method: 'resend'
        },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: [{ type: 'InviteList', id: 'LIST' }],
    }),
    sendInvitations: builder.mutation({
      query: ({ byWhat, expires, courseIds, withWhat, circleId }) => {
        let method, data;

        if (byWhat === 'groups') {
          method = 'byCircleIds';
          data = { circleIds: withWhat, courseIds, expires };
        } else if (byWhat === 'users') {
          method = 'byUserIds';
          data = { userIds: withWhat, courseIds, expires };
        } else {
          method = 'byEmails';
          data = { emails: withWhat, courseIds, expires, circleId };
        }

        return {
          url: inviteApi(),
          method: 'POST',
          body: {
            data,
            method
          },
        };
      },
      transformResponse: (response) => response.data,
      transformErrorResponse: (response) => {
        if (response.status === 'PARSING_ERROR' && response.data && typeof response.data === 'string') {
          return { error: 'Server returned invalid response format' };
        }
        return response;
      },
      invalidatesTags: [{ type: 'InviteList', id: 'LIST' }],
    }),
    invitesModalState: builder.query({
      queryFn: () => Promise.resolve({
        data: {
          isInviteModalOpen: false,
          modalProps: {},
          status: 'idle',
          sendStatus: 'idle',
          checkStatus: 'idle',
          redeemInvitation: {},
          giftCodeError: null,
          pendingGiftCode: null,
        }
      }),
      providesTags: ['InvitesModalState'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGenerateGiftCodesMutation,
  useCheckInvitationMutation,
  useAcceptInvitationMutation,
  useFetchInvitationsQuery,
  useFetchInviteByIdQuery,
  useDeleteInviteMutation,
  useResendInviteMutation,
  useSendInvitationsMutation,
  useInvitesModalStateQuery,
} = invitesApi;
